<script>
import Tab from './Tab.vue';

export default {
  name: 'TabGroup',
  components: {
    Tab,
  },
  props: {
    tabTitles: {
      type: Array,
      required: true,
    },
    tabContent: {
      type: Array,
      required: true,
    },
    preselectedTab: {
      type: [Number, String],
      default: 0,
    },
    isNavigation: {
      type: Boolean,
      default: false,
    },
    urls: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      tabs: [],
      activeTabIndex: this.preselectedTab || 0,
      showTabIndicator: false,
      showBothIndicators: false,
      showLeftOnly: false,
    };
  },
  created() {
    const { tabTitles, tabContent, urls, activeTabIndex, isObjEmpty } = this;

    if (tabTitles.length === tabContent.length) {
      for (const [index, content] of tabContent.entries()) {
        const tabObject = {
          content,
          url: urls ? urls[index] : '',
          title: isObjEmpty(content) ? null : tabTitles[index],
        };

        // If tab content is empty, set next tab active
        if (activeTabIndex === index && content.length === 0) {
          this.activeTabIndex = activeTabIndex + 1;
        }

        if (typeof content !== 'object') {
          return false;
        } else {
          this.tabs.push(tabObject);
        }
      }
    }
    // why not destructuring here?
    // because during looping above, properties could be reassigned.
    const activeTabIndexIsZero = this.activeTabIndex >= this.tabs.length || this.activeTabIndex < 0;
    this.activeTabIndex = activeTabIndexIsZero ? 0 : this.activeTabIndex;
  },
  mounted() {
    this.toggleTabIndicator();
    window.addEventListener('resize', this.toggleTabIndicator);
  },
  methods: {
    updateActiveTab: function(tabId, tabElem) {
      this.activeTabIndex = tabId;
      this.updateActiveTabPosition(tabElem);
    },
    slideFunc: function(scrollElem, scrollAmountMax, isScrollLeft) {
      let scrollAmount = 0;
      const slideTimer = setInterval(() => {
        if (isScrollLeft) {
          scrollElem.scrollLeft += 10;
        } else {
          scrollElem.scrollLeft -= 10;
        }
        scrollAmount += 10;
        if (scrollAmount >= scrollAmountMax) {
          window.clearInterval(slideTimer);
        }
      }, 10);
    },
    updateActiveTabPosition: function(targetEle) {
      const tabGroup = this.$refs.tabGroup;
      const wrapWidth = tabGroup.clientWidth;
      const tabWidth = targetEle.clientWidth;
      const offsetLeft = targetEle.getClientRects()[0].left;

      // tab in the left side
      if (offsetLeft < 30) {
        const scrollAmountMax = -1 * offsetLeft + 30;
        this.slideFunc(tabGroup, scrollAmountMax, false);
      }

      // tab in the right side
      if (offsetLeft + tabWidth > wrapWidth) {
        const scrollAmountMax = offsetLeft + tabWidth - wrapWidth + 30;
        this.slideFunc(tabGroup, scrollAmountMax, true);
      }
    },
    isObjEmpty: function(obj) {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          return false;
        }
      }
      return true;
    },
    isActive: function(tabId) {
      return this.activeTabIndex === tabId;
    },
    toggleTabIndicator: function() {
      const tabGroup = this.$refs.tabGroup;
      if (tabGroup === undefined) return;

      const tabGroupWidth = tabGroup.clientWidth;
      const scrollWidth = tabGroup.scrollWidth;
      if (scrollWidth <= tabGroupWidth) {
        this.showTabIndicator = false;
      } else {
        this.showTabIndicator = true;
      }

      const self = this;
      tabGroup.addEventListener('scroll', function(e) {
        const horizontal = parseInt(e.currentTarget.scrollLeft);
        const isReachRightEnd = horizontal + tabGroupWidth >= scrollWidth;

        if (horizontal > 5 && !isReachRightEnd) {
          // scroll in between
          self.showBothIndicators = true;
          self.showLeftOnly = false;
        } else if (horizontal > 5 && isReachRightEnd) {
          // reach the right end
          self.showLeftOnly = true;
          self.showBothIndicators = false;
        } else {
          // default position
          self.showBothIndicators = false;
        }
      });
    },
  },
};
</script>

<template>
  <div class="tab-content-container">
    <div
      v-if="tabs.length > 0"
      :class="{
        'tap-group--show-indicator': showTabIndicator,
        'tap-group--show-both-indicators': showBothIndicators,
        'tap-group--show-left-only': showLeftOnly
      }"
      class="tab-content-wrapper"
    >
      <div v-show="tabs.length > 1" ref="tabGroup" class="tab-group">
        <template v-for="(tab, index) in tabs">
          <Tab
            v-if="tab.title"
            :id="index"
            :title="tab.title"
            :is-active="isActive(index)"
            :key="`tab-${index}`"
            :is-navigation="isNavigation"
            :url="tab.url"
            @setActiveTab="updateActiveTab"
          />
        </template>
      </div>
      <div class="tab-arrow tab-arrow--left"></div>
      <div class="tab-arrow tab-arrow--right"></div>
      <div class="tab-content-wrapper">
        <template v-for="(tab, index) in tabs">
          <slot v-if="isActive(index)" :name="'tab-content-' + index" class="tab-content"></slot>
        </template>
      </div>
    </div>
  </div>
</template>
